import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { FunctionQueueStatus, FunctionRun } from '../../core/api/models/function-run.model';
import { FunctionRunsApiService } from '../../core/api/services/function-runs-api.service';
import { localDateInputToUtcRange } from '../../core/date/local-day-range';
import { formatRunDuration } from '../../shared/ui/duration/duration';
import { environment } from '../../../environments/environment';
import {
  emptyRunFilters,
  RunFilterValue,
  RunFiltersComponent,
} from '../../shared/ui/run-filters/run-filters.component';

@Component({
  selector: 'app-function-queue',
  standalone: true,
  imports: [CommonModule, RouterLink, RunFiltersComponent],
  templateUrl: './function-queue.component.html',
  styleUrl: './function-queue.component.scss',
})
export class FunctionQueueComponent implements OnInit, OnDestroy {
  private readonly api = inject(FunctionRunsApiService);
  // TEMPORARY: expose cancellation to every admin-panel user until API role policies are restored.
  // Restore AdminOperator gating here when the backend [Authorize(Policy = "AdminOperator")] is enabled.
  readonly canOperate = true;
  readonly activeStatuses: FunctionQueueStatus[] = ['Pending', 'Running', 'Cancelling', 'Retrying'];
  runs: FunctionRun[] = [];
  filters: RunFilterValue = emptyRunFilters();
  loading = false;
  error = '';
  notice = '';
  cancellingRunId: number | null = null;
  private timer?: number;

  get filteredRuns(): FunctionRun[] {
    const search = this.filters.search.toLowerCase();
    const range = localDateInputToUtcRange(this.filters.fromDate, this.filters.toDate);
    const from = range.fromUtc ? Date.parse(range.fromUtc) : null;
    const to = range.toUtc ? Date.parse(range.toUtc) : null;
    return this.runs.filter(run => {
      const queued = Date.parse(run.queuedAtUtc);
      return (!search || run.functionName.toLowerCase().includes(search) || run.url.toLowerCase().includes(search))
        && (!this.filters.source || run.source === this.filters.source)
        && (!this.filters.functionType || run.functionType === this.filters.functionType)
        && (!this.filters.triggerSource || run.triggerSource === this.filters.triggerSource)
        && (!this.filters.status || run.status === this.filters.status)
        && (from === null || queued >= from)
        && (to === null || queued < to);
    });
  }

  ngOnInit(): void {
    this.load();
    this.timer = window.setInterval(() => this.load(), environment.refreshIntervals.queueMilliseconds);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      window.clearInterval(this.timer);
    }
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api.getQueue().pipe(finalize(() => this.loading = false)).subscribe({
      next: value => this.runs = value,
      error: () => this.error = 'Could not load queue.',
    });
  }

  applyFilters(filters: RunFilterValue): void {
    this.filters = filters;
  }

  cancel(run: FunctionRun): void {
    if (run.status === 'Cancelling' || this.cancellingRunId !== null) return;
    if (!window.confirm('Cancel this run? Remaining work will stop, but changes already applied to external systems cannot be undone.')) {
      return;
    }

    this.cancellingRunId = run.id;
    this.error = '';
    this.notice = '';
    this.api.cancelRun(run.id).pipe(finalize(() => this.cancellingRunId = null)).subscribe({
      next: value => {
        this.notice = value.status === 'Cancelling'
          ? 'Cancellation requested. The running task is stopping.'
          : 'Run cancelled.';
        this.load();
      },
      error: error => this.error = error.error?.message || 'Could not cancel run.',
    });
  }

  canCancel(run: FunctionRun): boolean {
    return run.status === 'Pending' || run.status === 'Running' || run.status === 'Retrying';
  }

  statusClass(status: FunctionQueueStatus): string {
    return `status ${status.toLowerCase()}`;
  }

  duration(run: FunctionRun): string {
    return formatRunDuration(run.startedAtUtc, run.completedAtUtc);
  }
}
